import * as api from '@gitbook/api-client';
import {
    checkIsFreeProductKey,
    checkIsUnrestrictedProductKey,
    type NonLegacySiteType,
} from '@gitbook/app-pricing';
import { reportException } from '@gitbook/util-debug';

/**
 * Returns the monthly price for the given product or site.
 * We always display monthly prices to users, but the actual monthly cost varies
 * based on billing interval (yearly prices are discounted).
 */
function getMonthlyPrice({
    product,
    category,
    pricing,
    interval,
}: {
    pricing: api.OrganizationPricing;
    interval: api.BillingInterval;
} & (
    | {
          product: api.BillingProduct;
          category: 'members';
      }
    | {
          product: NonLegacySiteType;
          category: 'sites';
      }
)) {
    try {
        return pricing[category][product][interval];
    } catch {
        // It should never happen for a product to be missing from the pricing
        // but we handle it here in case the client is out of sync. For example,
        // immediately after deprecating a product. Let's report it to monitor it.
        reportException(
            new Error(`getMonthlyPrice: missing price for ${product} ${category} ${interval}`)
        );

        return null;
    }
}

/**
 * Returns the monthly price for the given site type.
 */
export function getMonthlySitePrice(params: {
    siteType: api.SiteType.LegacyBasic | api.SiteType.LegacyPremium;
    billing: api.OrganizationBilling;
    interval?: api.BillingInterval;
}): null;

export function getMonthlySitePrice(params: {
    siteType: Exclude<api.SiteType, api.SiteType.LegacyBasic | api.SiteType.LegacyPremium>;
    billing: api.OrganizationBilling;
    interval?: api.BillingInterval;
}): number;

export function getMonthlySitePrice(params: {
    siteType: api.SiteType;
    billing: api.OrganizationBilling;
    interval?: api.BillingInterval;
}): number | null;

export function getMonthlySitePrice({
    siteType,
    billing,
    interval,
}: {
    siteType: api.SiteType;
    billing: api.OrganizationBilling;
    interval?: api.BillingInterval;
}) {
    // Legacy sites do not have prices
    if (siteType === api.SiteType.LegacyBasic || siteType === api.SiteType.LegacyPremium) {
        return null;
    }

    // Basic and sponsored sites are free
    if (siteType === api.SiteType.Basic || siteType === api.SiteType.Sponsored) {
        return 0;
    }

    return getMonthlyPrice({
        product: siteType,
        category: 'sites',
        pricing: billing.pricing,
        interval: interval ?? billing.interval,
    });
}

/**
 * Returns the monthly price for the given product.
 */
export function getMonthlyMemberPrice(params: {
    product:
        | api.BillingProduct.Enterprise2024
        | api.BillingProduct.Community2024
        | api.BillingProduct.Internal;
    billing: api.OrganizationBilling;
    interval?: api.BillingInterval;
}): null;

export function getMonthlyMemberPrice(params: {
    product: Exclude<
        api.BillingProduct,
        | api.BillingProduct.Enterprise2024
        | api.BillingProduct.Community2024
        | api.BillingProduct.Internal
    >;
    billing: api.OrganizationBilling;
    interval?: api.BillingInterval;
}): number;

export function getMonthlyMemberPrice(params: {
    product: api.BillingProduct;
    billing: api.OrganizationBilling;
    interval?: api.BillingInterval;
}): number | null;

export function getMonthlyMemberPrice({
    product,
    billing,
    interval,
}: {
    product: api.BillingProduct;
    billing: api.OrganizationBilling;
    interval?: api.BillingInterval;
}) {
    // Unrestricted orgs have custom pricing
    if (checkIsUnrestrictedProductKey(product)) {
        return null;
    }

    // Free plans are... free
    if (checkIsFreeProductKey(product)) {
        return 0;
    }

    return getMonthlyPrice({
        product,
        category: 'members',
        pricing: billing.pricing,
        interval: interval ?? billing.interval,
    });
}
